import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Search, Sparkles, Bot } from "lucide-react";
import SEO from "../../components/SEO";
import MarketingNav from "../../components/marketing/MarketingNav";
import MarketingFooter from "../../components/marketing/MarketingFooter";
import { ALTERNATIVES } from "../../data/alternatives";

export default function AlternativesIndex() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Collavue alternatives",
    description:
      "Compare Collavue with the most popular X (Twitter), LinkedIn, and Reddit lead generation tools.",
    hasPart: ALTERNATIVES.map((a) => ({
      "@type": "WebPage",
      name: `Collavue vs ${a.name}`,
      url: `https://collavue.com/alternatives/${a.slug}`,
    })),
  };

  return (
    <div className="bg-[#faf6ee] text-[#0a1736] min-h-screen flex flex-col">
      <SEO
        title="Best Alternatives to X, LinkedIn & Reddit Social Lead Tools"
        description="Compare Collavue with GummySearch, Devi AI, F5Bot, Subreddit Signals, ReplyGuy, RedReach, OctoLens, Syften, Brand24, KWatch.io, Mention, RedShip, Tydal, and Linkeddit. See feature-by-feature breakdowns."
        path="/alternatives"
        keywords="GummySearch alternative, Devi AI alternative, F5Bot alternative, X lead generation, Twitter lead generation, LinkedIn lead generation, Reddit lead generation, social listening alternatives, B2B social listening"
        schema={schema}
      />
      <MarketingNav />

      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10 opacity-60"
          style={{
            backgroundImage:
              "radial-gradient(1200px 400px at 80% -20%, rgba(45,91,255,0.10), transparent 60%)",
          }}
        />
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-12 lg:pt-24">
          <p className="text-[#2d5bff] font-medium text-sm tracking-wide uppercase">
            Alternatives
          </p>
          <h1 className="font-display text-[#0a1736] text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05] mt-4 max-w-4xl">
            The best alternatives to X (Twitter), LinkedIn, and Reddit social lead tools in 2026.
          </h1>
          <p className="mt-6 text-lg text-[#5a6478] max-w-3xl leading-relaxed">
            Looking for a better way to find and reply to people asking for what you offer?
            We've put Collavue head-to-head with the tools you've probably already evaluated.
            Pick the comparison that matches what you're using today.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ALTERNATIVES.map((a) => (
              <Link
                key={a.slug}
                to={`/alternatives/${a.slug}`}
                className="group rounded-xl border border-[#e3dccb] bg-white p-5 hover:border-[#2d5bff] hover:shadow-[0_10px_40px_-25px_rgba(45,91,255,0.45)] transition-all"
              >
                <div className="flex items-center justify-between">
                  <span className="font-display text-xl text-[#0a1736] font-semibold">
                    Alternative to {a.name}
                  </span>
                  <ArrowUpRight className="w-4 h-4 text-[#2d5bff] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </div>
                <p className="mt-2 text-sm text-[#5a6478] leading-relaxed">{a.tagline}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white border-t border-[#e3dccb]">
        <div className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-3 gap-6">
          <Pillar icon={Search} title="Discover" body="X (Twitter), LinkedIn, and Reddit coverage from one inbox — built for where B2B buyers actually vent and post." />
          <Pillar icon={Sparkles} title="Score" body="Intent + ICP-fit scoring out of 100, so you only spend time on the 5% worth replying to." />
          <Pillar icon={Bot} title="Reply" body="On-brand drafts, human approval, and outcome tracking from reply to booked demo." />
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}

function Pillar({ icon: Icon, title, body }) {
  return (
    <div className="rounded-xl border border-[#e3dccb] p-6 bg-[#faf6ee]/40">
      <Icon className="w-6 h-6 text-[#2d5bff]" />
      <h3 className="font-display text-xl text-[#0a1736] font-semibold mt-3">{title}</h3>
      <p className="text-[#5a6478] text-sm mt-2 leading-relaxed">{body}</p>
    </div>
  );
}
